import React, { createContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import collect from 'collect.js';
import useApiClient from '../../hooks/api/useApiClient';
import usePermissions, { ManagerRole } from '../../hooks/auth/usePermissions';
import { dateRangeKey } from '../../services/helpers/DateRangeHelpers';

export const StatisticsContext = createContext(null);

const CHUNK_SIZE = 10;

function isServerFailure(response) {
  return response?.success === false && (!response.status || response.status >= 500);
}

export default function StatisticsProvider({
  children,
  mainMetrics = [],
  reservationMetrics = [],
  marketingMetrics = [],
  googleBusinessMetrics = [],
  googleAdsMetrics = [],
  storefrontMetrics = [],
  mobileAppMetrics = [],
  excludedHistoryMetrics = [],
  displayOrder = [],
  withInterval = false,
  dateRangeFilter,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Dashboard:Statistics' });

  const {
    StatsManager,
    GoogleBusinessManager,
    GoogleAdsManager,
    StorefrontManager,
    ReservationsManager,
    EmailMarketingManager,
    MobileAppStatsManager,
    MobileAppManager,
  } = useApiClient();

  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  const { company } = useSelector((store) => store.currentCompany);
  const { franchise } = useSelector((store) => store.currentFranchise);
  const { hasAnyRole } = usePermissions();
  const isMerchant = hasAnyRole([ManagerRole.StoreAdmin, ManagerRole.StoreEmployee, ManagerRole.StoreOperator], false);

  const hasRestaurantGroupStats = typeof company?.id === 'number' || typeof franchise?.id === 'number' || isMerchant;

  const [stats, setStats] = useState({});

  const statsRef = useRef(stats);
  statsRef.current = stats;

  const [loading, setLoading] = useState(null);

  const [loadingKeys, dispatchLoadingKeys] = useReducer((state, action) => {
    switch (action.type) {
      case 'ADD':
        return new Set([...state, action.route]);
      case 'REMOVE': {
        const newState = new Set(state);
        newState.delete(action.route);

        return newState;
      }

      default:
        return state;
    }
  }, new Set());

  const [loadingMetrics, dispatchLoadingMetrics] = useReducer((state, action) => {
    switch (action.type) {
      case 'ADD':
        return new Set([...state, ...action.metrics]);
      case 'REMOVE': {
        const newState = new Set(state);
        action.metrics.forEach((metric) => newState.delete(metric));

        return newState;
      }

      default:
        return state;
    }
  }, new Set());

  const ROUTES = {
    MAIN: 'main',
    Reservations: 'reservations',
    Marketing: 'marketing',
    MobileApp: 'mobile_app',
    GOOGLE_BUSINESS: 'google_business',
    GOOGLE_ADS: 'google_ads',
    Storefront: 'storefront',
  };

  function getPayload(values) {
    return {
      with_interval: withInterval,
      ...dateRangeFilter,
      ...values,
    };
  }

  const METRICS = {
    [ROUTES.MAIN]: {
      metrics: mainMetrics,
      // Shop computes each metric family once per request and shares it across the
      // metrics built on it, so splitting the page only repeats those queries and the
      // per-request auth cost. Provider-wide /stats stays chunked: its cold chunks take
      // 6-11 s each in parallel, which run serially could pass 30 s. The other routes
      // proxy to services whose limits are not verified, so they stay chunked too.
      batched: hasRestaurantGroupStats,
      get: (metricsChunk) => {
        if (hasRestaurantGroupStats) {
          return StatsManager.getCompanyStats(
            getPayload({
              franchise_id: company.id ? company.franchise_id : franchise?.id,
              companies: company.id ? [company.id] : ['*'],
              metrics: metricsChunk,
            }),
          );
        }

        return StatsManager.getStats(
          getPayload({
            metrics: metricsChunk,
          }),
        );
      },
    },
    [ROUTES.Reservations]: {
      metrics: reservationMetrics,
      get: (metricsChunk) => {
        if (hasRestaurantGroupStats) {
          return ReservationsManager.getCompanyStats(
            getPayload({
              franchise_id: company.id ? company.franchise_id : franchise?.id,
              companies: company.id ? [company.id] : ['*'],
              metrics: metricsChunk,
            }),
          );
        }

        return ReservationsManager.getStats(
          getPayload({
            service_provider_id: serviceProvider?.id,
            metrics: metricsChunk,
          }),
        );
      },
    },
    [ROUTES.Marketing]: {
      metrics: marketingMetrics,
      get: (metricsChunk) => {
        if (hasRestaurantGroupStats) {
          return EmailMarketingManager.getCompanyStats(
            getPayload({
              franchise_id: company.id ? company.franchise_id : franchise?.id,
              companies: company.id ? [company.id] : ['*'],
              metrics: metricsChunk,
            }),
          );
        }

        return Promise.resolve({ data: {} });
      },
    },
    [ROUTES.GOOGLE_BUSINESS]: {
      metrics: googleBusinessMetrics,
      get: (metricsChunk) => {
        if (hasRestaurantGroupStats) {
          return GoogleBusinessManager.getCompanyStats(
            getPayload({
              franchise_id: company.id ? company.franchise_id : franchise?.id,
              companies: company.id ? [company.id] : ['*'],
              metrics: metricsChunk,
            }),
          );
        }

        return Promise.resolve({ data: {} });
      },
    },
    [ROUTES.GOOGLE_ADS]: {
      metrics: googleAdsMetrics,
      get: () => {
        if (hasRestaurantGroupStats) {
          return GoogleAdsManager.getCompanyStats(
            getPayload({
              franchise_id: company.id ? company.franchise_id : franchise?.id,
              companies: company.id ? [company.id] : ['*'],
            }),
          );
        }

        return Promise.resolve({ data: {} });
      },
    },
    [ROUTES.Storefront]: {
      metrics: storefrontMetrics,
      get: (metricsChunk) => {
        if (hasRestaurantGroupStats) {
          return StorefrontManager.getStats(
            getPayload({
              franchise_id: company.id ? company.franchise_id : franchise?.id,
              companies: ['*'],
              metrics: metricsChunk,
            }),
          );
        }

        return Promise.resolve({ data: {} });
      },
    },
    [ROUTES.MobileApp]: {
      metrics: mobileAppMetrics,
      get(metricsChunk) {
        if (hasRestaurantGroupStats) {
          return MobileAppStatsManager.get(
            getPayload({
              franchise_id: company.id ? company.franchise_id : franchise?.id,
              metrics: metricsChunk,
            }),
          );
        }

        return MobileAppManager.getStats(
          getPayload({
            metrics: metricsChunk,
          }),
        );
      },
    },
  };

  const metrics = useMemo(
    () =>
      collect(METRICS)
        .map((_, route) =>
          collect(METRICS[route].metrics)
            .filter((metric) => !excludedHistoryMetrics.includes(metric))
            .map((metric) => ({ metric, route })),
        )
        .flatten(1)
        .sortBy(({ metric }) => {
          const index = displayOrder.indexOf(metric);

          return index === -1 ? Number.MAX_VALUE : index;
        })
        .toArray(),
    [],
  );

  const requestIds = useRef({});

  function applyStats(routeMetrics, responseData) {
    setStats((prevStats) => ({
      ...prevStats,
      ...collect(routeMetrics)
        .mapWithKeys((metric) => [metric, responseData?.[metric]])
        .all(),
    }));

    dispatchLoadingMetrics({ type: 'REMOVE', metrics: routeMetrics });
  }

  async function requestChunks(route, routeMetrics, isStale) {
    const chunks = collect(routeMetrics).chunk(CHUNK_SIZE).toArray();

    await Promise.all(
      chunks.map(async (chunk) => {
        const { data: responseData } = await METRICS[route].get(chunk);

        if (!isStale()) {
          applyStats(chunk, responseData);
        }
      }),
    );
  }

  async function requestStatistic(route) {
    if (METRICS[route].metrics.length === 0) {
      return;
    }

    const requestId = (requestIds.current[route] ?? 0) + 1;
    requestIds.current[route] = requestId;
    // A response for an earlier filter must not overwrite the current one or end its loading state.
    const isStale = () => requestIds.current[route] !== requestId;

    setLoading(true);
    dispatchLoadingKeys({ type: 'ADD', route });

    const routeMetrics = METRICS[route].metrics;
    dispatchLoadingMetrics({ type: 'ADD', metrics: routeMetrics });

    if (METRICS[route].batched) {
      const response = await METRICS[route].get(routeMetrics);

      if (isStale()) {
        return;
      }

      // One metric that throws fails the whole request; retrying in chunks confines the blank tiles to its chunk.
      if (isServerFailure(response)) {
        await requestChunks(route, routeMetrics, isStale);
      } else {
        applyStats(routeMetrics, response?.data);
      }
    } else {
      await requestChunks(route, routeMetrics, isStale);
    }

    if (isStale()) {
      return;
    }

    setLoading(false);
    dispatchLoadingKeys({ type: 'REMOVE', route });
  }

  /**
   * The filter's value, so a new but equal filter object (callers build one on re-renders and
   * re-selections) does not resend every route. It covers period, start_date and end_date only;
   * any other field on the filter does not trigger a refetch.
   */
  const filterKey = dateRangeFilter ? dateRangeKey(dateRangeFilter) : null;

  useEffect(() => {
    if (dateRangeFilter) {
      collect(METRICS)
        .keys()
        .each((route) => requestStatistic(route));
    }
  }, [filterKey, serviceProvider?.id, company?.id, franchise?.id]);

  const context = useMemo(
    () => ({
      stats: statsRef.current,
      metrics,
      isLoading: (route) => loadingKeys.has(route),
      isMetricLoading: (metricNames) => {
        if (Array.isArray(metricNames)) {
          return metricNames.some((metric) => loadingMetrics.has(metric));
        }

        return loadingMetrics.has(metricNames);
      },
      getMetricLabel: (metric, values = {}) => t(`fields.${metric}.label`, values),
      getMetric: (metric) => collect(statsRef.current).get(metric),
      dateRangeFilter,
      loading,
      loadingMain: loadingKeys.has(ROUTES.MAIN),
      loadingReservations: loadingKeys.has(ROUTES.Reservations),
      loadingMarketing: loadingKeys.has(ROUTES.Marketing),
      loadingGoogleBusiness: loadingKeys.has(ROUTES.GOOGLE_BUSINESS),
      loadingGoogleAds: loadingKeys.has(ROUTES.GOOGLE_ADS),
      loadingMobileApp: loadingKeys.has(ROUTES.MobileApp),
      loadingStorefront: loadingKeys.has(ROUTES.Storefront),
    }),
    [statsRef.current, metrics, t, loadingKeys, loadingMetrics, loading, dateRangeFilter],
  );

  return <StatisticsContext.Provider value={context}>{children}</StatisticsContext.Provider>;
}
